<script>
import NextButton from 'dashboard/components-next/button/Button.vue';
import ClassificationBadge from 'dashboard/routes/dashboard/comments/components/ClassificationBadge.vue';
import MetadataBadge from 'dashboard/routes/dashboard/comments/components/MetadataBadge.vue';
import CustomClassificationBadge from 'dashboard/routes/dashboard/comments/components/CustomClassificationBadge.vue';
import { mapGetters, mapActions } from 'vuex';
import CustomIcon from 'dashboard/components/ui/CustomIcons.vue';
import AITestHistoryDrawer from 'dashboard/routes/dashboard/comments/components/AITestHistoryDrawer.vue';

export default {
  name: 'AITestChat',
  components: {
    NextButton,
    ClassificationBadge,
    MetadataBadge,
    CustomClassificationBadge,
    CustomIcon,
    AITestHistoryDrawer,
  },
  props: {
    post: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      commentToSend: '',
      isLoading: false,
      showHistory: false,
    };
  },
  computed: {
    ...mapGetters({
      testMode: 'comments/getTestModeInfo',
      uiFlags: 'comments/getUIFlags',
    }),
    personMetadataType() {
      return metadata => {
        if (metadata?.purchase_intention) {
          return 'PURCHASE_INTENTION';
        }
        return null;
      };
    },
    aiMetadataType() {
      return metadata => {
        if (metadata?.purchase_intention) {
          return 'PURCHASE_INTENTION';
        }
        return null;
      };
    },
    showPersonMetadataBadge() {
      return (
        this.testMode?.person_comment?.from_name &&
        this.testMode?.person_comment?.metadata?.purchase_intention
      );
    },
    showAiMetadataBadge() {
      return this.testMode?.ai_comment?.metadata?.purchase_intention;
    },
    personCustomClassifications() {
      return (
        this.testMode?.person_comment?.metadata?.custom_classifications || []
      );
    },
    aiCustomClassifications() {
      return this.testMode?.ai_comment?.metadata?.custom_classifications || [];
    },
  },
  mounted() {
    this.clearTestMode();
  },
  methods: {
    ...mapActions({
      testModeAction: 'comments/testMode',
      clearTestMode: 'comments/clearTestMode',
    }),

    sendComment() {
      if (!this.commentToSend.trim()) return;

      this.setShowHistory(false);
      this.clearTestMode();
      this.testModeAction({
        pageId: this.post.channel_facebook_page_id,
        postId: this.post.id,
        message: this.commentToSend,
      });

      this.commentToSend = '';
    },
    setShowHistory(value) {
      if (this.uiFlags.testMode.isFetchingTestMode) return;
      this.showHistory = value;
    },
  },
};
</script>

<template>
  <div class="p-4 mb-2">
    <span class="block mb-2 text-sm font-medium text-slate-900 dark:text-white">
      {{ 'Envie um comentário para testar as respostas da IA:' }}
    </span>

    <textarea
      v-model="commentToSend"
      class="w-full mt-4 mb-2 px-2 border rounded dark:text-white"
      rows="3"
      placeholder="Digite um comentário..."
    />

    <div class="flex justify-between w-full">
      <NextButton
        label="Enviar"
        :disabled="!commentToSend.trim() || uiFlags.testMode.isFetchingTestMode"
        class="mt-2"
        @click="sendComment"
      />
      <NextButton
        label="Ver Histórico"
        :disabled="uiFlags.testMode.isFetchingTestMode || showHistory"
        class="mt-2"
        @click="setShowHistory(true)"
      />
    </div>

    <div
      v-if="uiFlags.testMode.isFetchingTestMode"
      class="mt-4 flex items-center space-x-2"
    >
      <CustomIcon group="general" name="LOADING" color="#5A6169" />
      <span class="text-sm text-slate-500 dark:text-slate-400">{{
        'A IA está respondendo...'
      }}</span>
    </div>
    <div
      v-if="
        !uiFlags.testMode.isFetchingTestMode &&
        testMode &&
        testMode.person_comment?.id
      "
      :key="testMode.person_comment?.id"
      class="mt-4 space-y-3"
    >
      <div class="p-3 border rounded dark:bg-slate-700">
        <div>
          <div class="flex flex-col w-full gap-2">
            <span class="text-sm font-semibold text-slate-900 dark:text-white">
              {{ testMode.person_comment.from_name }}
            </span>
            <div class="flex flex-wrap items-center gap-2">
              <div
                v-if="testMode.person_comment.classification"
                :key="testMode.person_comment.classification"
              >
                <ClassificationBadge
                  :type="testMode.person_comment.classification"
                />
              </div>
              <div v-else>
                {{ 'Processando Classificação' }}
              </div>
              <MetadataBadge
                v-if="showPersonMetadataBadge"
                :type="personMetadataType(testMode.person_comment.metadata)"
              />
              <CustomClassificationBadge
                v-if="
                  Array.isArray(personCustomClassifications) &&
                  personCustomClassifications.length
                "
                :items="personCustomClassifications"
                :max-visible="2"
              />
            </div>
          </div>
          <p class="text-sm text-slate-800 dark:text-slate-300">
            {{ testMode.person_comment.message }}
          </p>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            {{ new Date(testMode.person_comment.created_at).toLocaleString() }}
          </p>
        </div>
      </div>

      <div
        v-if="testMode.ai_comment.classification"
        class="p-3 border rounded dark:bg-slate-700"
      >
        <div>
          <div class="flex flex-col w-full gap-2">
            <span class="text-sm font-semibold text-slate-900 dark:text-white">
              {{ 'Resposta da IA' }}
            </span>
            <div class="flex flex-wrap items-center gap-2">
              <div
                v-if="testMode.ai_comment.classification"
                :key="testMode.ai_comment.classification"
              >
                <ClassificationBadge
                  :type="testMode.ai_comment.classification"
                />
              </div>
              <div v-else>
                {{ 'Processando Classificação' }}
              </div>
              <MetadataBadge
                v-if="showAiMetadataBadge"
                :type="aiMetadataType(testMode.ai_comment.metadata)"
              />
              <CustomClassificationBadge
                v-if="
                  Array.isArray(aiCustomClassifications) &&
                  aiCustomClassifications.length
                "
                :items="aiCustomClassifications"
                :max-visible="2"
              />
            </div>
          </div>
          <p class="text-sm text-slate-800 dark:text-slate-300">
            {{ testMode.ai_comment.message }}
          </p>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            {{ new Date(testMode.ai_comment.created_time).toLocaleString() }}
          </p>
        </div>
      </div>
    </div>
    <div
      v-if="uiFlags.testMode.hasError"
      :key="uiFlags.testMode.hasError"
      class="mt-4"
    >
      {{
        'Ops... Ocorreu um erro ao processar sua resposta. Tente novamente mais tarde'
      }}
    </div>
  </div>
  <AITestHistoryDrawer v-model:show-history="showHistory" :post="post" />
</template>
